import {CompletionContext, snippet, Completion} from '@codemirror/autocomplete';

// Define completions by context
const keywordCompletions: Completion[] = [
    { label: 'class', type: 'keyword' },
    { label: 'def', type: 'keyword' },
    { label: 'return', type: 'keyword' },
    { label: 'import', type: 'keyword' },
    { label: 'from', type: 'keyword' },
    { label: 'if', type: 'keyword' },
    { label: 'elif', type: 'keyword' },
    { label: 'else', type: 'keyword' },
    { label: 'for', type: 'keyword' },
    { label: 'while', type: 'keyword' },
    { label: 'try', type: 'keyword' },
    { label: 'except', type: 'keyword' },
    { label: 'pass', type: 'keyword' },
    { label: 'self', type: 'keyword' },
    { label: 'None', type: 'keyword' },
    { label: 'True', type: 'keyword' },
    { label: 'False', type: 'keyword' },
];

const classCompletions: Completion[] = [
    {
        label: 'PositionChange',
        type: 'class',
        detail: 'PositionChange(trade_date: date, ticker: str, price: float, action: str, shares: float)',
        apply: snippet('PositionChange(${trade_date}, ${ticker}, ${price}, ${action}, ${shares})')
    },
    {
        label: 'Position',
        type: 'class',
        detail: 'Position(ticker: str, quantity: float, price: float)',
        apply: snippet('Position(${ticker}, ${quantity}, ${price})')
    },
    {
        label: 'Portfolio',
        type: 'class',
        detail: 'Portfolio(cash: float, positions: list[Position])',
        apply: snippet('Portfolio(${cash}, ${positions})')
    },
];

// self. completions (only top-level properties/methods available on self)
const selfCompletions: Completion[] = [
    { label: 'portfolio', type: 'property', detail: 'Portfolio' },
    { label: 'initialize', type: 'method', detail: '()' },
    { label: 'trading_logic', type: 'method', detail: '(date)' },
    { label: 'get_price_for_ticker_on_date', type: 'method', detail: '(ticker, date) -> float' },
];

// portfolio. completions
const portfolioCompletions: Completion[] = [
    { label: 'cash', type: 'property', detail: 'float' },
    { label: 'positions', type: 'property', detail: 'list[Position]' },
    { label: 'portfolio_value', type: 'property', detail: 'float' },
    { label: 'positions_value_without_cash', type: 'property', detail: 'float' },
    // these should be hidden
    // {
    //     label: 'buy',
    //     type: 'method',
    //     detail: '(ticker: str, quantity: float, price: float) -> bool',
    //     apply: snippet('buy(${ticker}, ${quantity}, ${price})')
    // },
    // {
    //     label: 'sell',
    //     type: 'method',
    //     detail: '(ticker: str, quantity: float, price: float) -> bool',
    //     apply: snippet('sell(${ticker}, ${quantity}, ${price})')
    // },
    {
        label: 'get_position',
        type: 'method',
        detail: '(ticker: str) -> Position | None',
        apply: snippet('get_position(${ticker})')
    },
];

// position. completions (for Position objects)
const positionCompletions: Completion[] = [
    { label: 'ticker', type: 'property', detail: 'str' },
    { label: 'quantity', type: 'property', detail: 'float' },
    { label: 'price', type: 'property', detail: 'float' },
];

// position_change. completions
const positionChangeCompletions: Completion[] = [
    { label: 'trade_date', type: 'property', detail: 'date' },
    { label: 'ticker', type: 'property', detail: 'str' },
    { label: 'price', type: 'property', detail: 'float' },
    { label: 'action', type: 'property', detail: 'str' },
    { label: 'shares', type: 'property', detail: 'float' },
];

export function pythonCompletions(context: CompletionContext) {
    // Check for member access (e.g., "self.", "self.portfolio.", "position.")
    const beforeCursor = context.state.doc.sliceString(0, context.pos);

    // Match patterns like "self.portfolio." or "self." or "position."
    const memberMatch = beforeCursor.match(/(\w+(?:\.\w+)*)\.(\w*)$/);

    if (memberMatch) {
        const objectPath = memberMatch[1]; // e.g., "self" or "self.portfolio"
        const partial = memberMatch[2];    // what user is typing after the dot
        const from = context.pos - partial.length;

        let options: Completion[] = [];

        if (objectPath === 'self') {
            options = selfCompletions;
        } else if (objectPath === 'self.portfolio' || objectPath.endsWith('portfolio')) {
            options = portfolioCompletions;
        } else if (objectPath.includes('position_change') || objectPath.includes('change')) {
            options = positionChangeCompletions;
        } else if (objectPath.includes('position') || objectPath.includes('pos')) {
            options = positionCompletions;
        }

        if (options.length > 0) {
            return { from, options };
        }
    }

    // Default: keywords and class names
    const word = context.matchBefore(/\w*/);
    if (!word || (word.from === word.to && !context.explicit)) return null;

    return {
        from: word.from,
        options: [...keywordCompletions, ...classCompletions],
    };
}
